export default {
  cad: {
    sectionTitle: 'CAD',
    previewMode: 'Preview mode',
    surface: 'Surface',
    wireframe: 'Wireframe',
    wireframeUnavailable: 'Wireframe preview not available yet',
  },
  visualization: {
    sectionTitle: 'Visualization',
    qualityMetric: 'Quality metric',
  },
  qualityMetric: {
    sicn: 'SICN (Ideal=1)',
    gamma: 'Gamma (Ideal=1)',
    skewness: 'Skewness',
    aspectRatio: 'Aspect ratio',
    minAngle: 'Minimum angle',
    nonOrthogonality: 'Non-orthogonality',
  },
  cellInfo: {
    element: 'Element #{{id}}',
    region: 'Region {{tag}}',
    type: {
      triangle: 'Triangle',
      quadrangle: 'Quadrangle',
      tetrahedron: 'Tetrahedron',
      hexahedron: 'Hexahedron',
      prism: 'Prism',
      pyramid: 'Pyramid',
      polyhedron: 'Polyhedron',
    },
  },
  meshViewer: {
    view: 'View',
    auto: 'Auto',
    autoWithSource: 'Auto ({{source}})',
    diagnosisStl: 'Diagnosis STL',
    serverStl: 'Server STL',
    fluidDomain: 'Fluid domain',
    fineStl: 'Fine STL',
    cadTessellation: 'CAD tessellation',
    extractedCavity: 'Extracted cavity',
    loadingExtractedCavity: 'Loading extracted cavity…',
    faceLabel: 'Face {{index}}',
    clickFaceToAddBl: 'Click face to add to BL',
    pickSurface: 'Pick surface: {{source}}',
    noFluidDomainFallback: 'Picking on the body surface, not the fluid cavity.',
    rendererError: 'Renderer error',
    webgpuUnsupported:
      'WebGPU is not supported in this browser. Try the latest Chrome or Edge.',
    webgpuUnavailableTitle: '3D viewer needs WebGPU',
    webgpuUnavailableBody:
      "Your browser couldn't start a GPU session, so the 3D viewer can't render. This is a browser or GPU setup issue — your mesh is fine.",
    webgpuStepGpuPage:
      'Open edge://gpu or chrome://gpu and check that WebGPU shows "Hardware accelerated".',
    webgpuStepAccel:
      'Turn on "Use graphics acceleration when available" in browser settings, then restart the browser.',
    webgpuStepDriver: 'Update your graphics driver.',
    webgpuStepRemote:
      'On Remote Desktop or a VM? Run locally — they usually expose no GPU.',
    webglUnavailableTitle: '3D view needs WebGL',
    webglUnavailableBody:
      "Your browser couldn't start WebGL, so this 3D view can't render. This is a browser or GPU setup issue — your results are fine.",
    webglStepGpuPage:
      'Open edge://gpu or chrome://gpu and check that WebGL shows "Hardware accelerated".',
    meshTooLargeTitle: 'Mesh too large to render',
    meshTooLargeBody:
      'This mesh has too many cells for the in-browser 3D viewer to load. The mesh generated successfully — download it (OpenFOAM, Fluent, or STL) to open in an external tool.',
    meshUnreadableTitle: 'Could not read this mesh',
    meshUnreadableBody:
      'The mesh generated successfully — the viewer could not read it. This is a viewer bug, not a problem with your mesh. Download it (OpenFOAM, Fluent, or STL) to open elsewhere, and please report the error line below.',
    uploadPrompt: 'Upload a CAD model to get started',
    loadingMesh: 'Loading mesh',
    loadingDiagnosisPreview: 'Loading diagnosis preview',
    loadingPreview: 'Loading preview',
    buildingCadPreview: 'Building CAD preview',
    previewUnavailableTitle: '3D preview unavailable',
    previewUnavailableHint:
      'You can still generate a mesh — only the preview is affected.',
    noDiagnosisStl:
      'This geometry has no diagnosis STL. Pick another view below.',
    noServerPreview:
      'This geometry has no server preview. Pick another view below.',
    noPreviewAvailable: 'No preview is available for this file.',
    previewProvenanceSimplify:
      'This version is a simplification candidate, not an uploaded file — it has no 3D preview until approved.',
    buildPreviewMeshing: 'Meshing… · {{stage}}',
    buildPreviewStageBlockmesh: 'domain grid',
    buildPreviewStageSnapped: 'surface (before layers)',
    buildPreviewBuilding: 'building',
    buildPreviewTooltip:
      'A live preview of the mesh while it builds — not the final result. “Domain grid” is the background block covering the meshing region; “surface” is the geometry captured just before boundary layers.',
  },
  viewerOverlay: {
    bodySolid: 'Solid',
    bodyXray: 'X-Ray',
    bodyHidden: 'Hidden',
    viewAlong: 'View along {{axis}}',
    toggleWireframe: 'Toggle Wireframe',
    modelSolid: 'Model: Solid',
    modelXray: 'Model: X-Ray',
    modelHidden: 'Model: Hidden',
    toggleClip: 'Toggle Clip',
    flipSide: 'Flip Side',
    flipNeedsClip: 'Enable clip to flip',
    sliceViewPlane: 'Slice View Plane',
    slicePosition: 'Slice position',
    fitView: 'Fit to view',
    clipCrinkleActive:
      'Crinkle cut: keeps whole cells. Toggle to a clean section plane.',
    clipSectionActive:
      'Section cut: a clean plane. Toggle back to a crinkle cut that keeps whole cells.',
    crinkleCut: 'Crinkle',
    sectionCut: 'Section',
  },
  controlsHelp: {
    show: 'Viewer controls',
    mouse: 'Mouse',
    keyboard: 'Keyboard',
    dragL: 'Drag L',
    dragR: 'Drag R',
    shiftDragL: 'Shift + Drag L',
    dragLR: 'Drag L + R',
    wheel: 'Wheel',
    click: 'Click',
    orbit: 'Orbit',
    pan: 'Pan',
    roll: 'Roll',
    zoom: 'Zoom toward cursor',
    pick: 'Inspect cell',
    lookAxis: 'Look down X / Y / Z',
    pitch: 'Pitch ±90°',
    yaw: 'Yaw ±90°',
    rollKey: 'Roll ±90°',
    slice: 'Slice at view plane',
    cycleBody: 'Cycle Solid / X-Ray / Hidden',
    wireframe: 'Toggle wireframe',
    clip: 'Section cut on/off',
    flip: 'Flip section side',
  },
  viewerLoading: {
    loadingMesh: 'Loading mesh...',
    loadingPreview: 'Loading preview...',
    parsingMesh: 'Parsing mesh...',
    downloadingMesh: 'Downloading mesh',
    cachingMesh: 'Caching mesh...',
    restoringMemory: 'Restoring memory...',
    loadingCached: 'Loading cached mesh {{received}} of {{total}} bytes',
    downloading: 'Downloading {{received}} of {{total}} bytes',
  },
  debugViewer: {
    loadModel: 'Load model',
    loading: 'Loading…',
    meshFileLabel: 'Mesh file',
    qualityFileLabel: 'Quality metrics file',
  },
  // Public `/whats-new` page chrome — page headers, section labels,
  // status badges. Content (per-version release-note items) stays
  // English by design so release cycles don't require triple-locale
  // rewrites; see whats-new/AGENTS.md.
  changelog: {
    backToApp: 'Back to app',
    title: 'Changelog & Roadmap',
    subtitle: "Release notes and what's coming next for Khorium MeshGen",
    roadmapHeader: 'Roadmap',
    releasedHeader: 'Released',
    badgeLatest: 'Latest',
    badgeUpcoming: 'Upcoming',
    sectionLabelNew: 'NEW',
    sectionLabelImproved: 'IMPROVED',
    sectionLabelFixed: 'FIXED',
    datePlanned: 'Planned',
  },
} as const;
